import cameraAnimation from "@client/animations/plan/Camera.json";
import cookingEggAnimation from "@client/animations/plan/Cooking egg.json";
import dayNightAnimation from "@client/animations/plan/day  night.json";
import decisionAnimation from "@client/animations/plan/Decision.json";
import eatRepeatAnimation from "@client/animations/plan/Eat & Repeat.json";
import eatingAnimation from "@client/animations/plan/I-Max_animation.json";
import loadingAnimation from "@client/animations/plan/Loading.json";
import ovenAnimation from "@client/animations/plan/Oven.json";
import rubiksCubeAnimation from "@client/animations/plan/Rubik's cube.json";
import saladAnimation from "@client/animations/plan/Salad.json";
import setGoalAnimation from "@client/animations/plan/Set goal.json";
import tasksAnimation from "@client/animations/plan/Tasks.json";
import rocketAnimation from "@client/animations/rocket.json";
import * as graphics from "@client/app/graphics";
import {SimpleIcon} from "@client/components/Icon";
import {ScrollContainer} from "@client/components/SimpleScene";
import * as dates from "@client/dates";
import * as auth from "@client/features/auth";
import {type MFPRoute, useMFPFlow} from "@client/features/mfp";
import * as profile from "@client/features/profile";
import * as push from "@client/features/push";
import * as forms from "@client/forms";
import {Lottie} from "@client/hacks";
import * as hardware from "@client/hardware";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {useAppDispatch, useAppSelector} from "@client/store";
import {
    AdjustmentsHorizontalIcon,
    ArrowPathIcon,
    ArrowRightIcon,
    BellIcon,
    CalendarDaysIcon,
    CameraIcon,
    ChartBarIcon,
    CheckIcon,
    ClockIcon,
    DocumentArrowUpIcon,
    EnvelopeIcon,
    LightBulbIcon,
    ScaleIcon,
    ShoppingCartIcon,
    WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import {classNames, server} from "@reactivated";
import {
    AnimatePresence,
    MotionValue,
    SVGMotionProps,
    Transition,
    motion,
    useTransform,
} from "motion/react";
import React from "react";
import {match} from "ts-pattern";

export interface ShellRenderProps {
    scrollY: MotionValue<number>;
    hasOverflow: boolean;
}

export function Shell(props: {
    children: React.ReactNode | ((renderProps: ShellRenderProps) => React.ReactNode);
}) {
    return (
        <div
            className="flex h-dvh w-full flex-col bg-cover bg-center bg-no-repeat"
            style={{
                backgroundImage: "url('/static/images/plan-onboarding-bg.jpeg')",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                backgroundBlendMode: "overlay",
            }}
        >
            <ScrollContainer className="mx-auto w-full max-w-3xl">
                {({scrollY, hasOverflow}) =>
                    typeof props.children === "function"
                        ? props.children({scrollY, hasOverflow})
                        : props.children
                }
            </ScrollContainer>
        </div>
    );
}

// CSS animation because AnimatePresence initial={false} suppresses nested motion animations.
const nudgeKeyframes = `
@keyframes nudge-x {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(3px); }
}
`;

const contentTransitionVariants = {
    initial: {opacity: 0, y: 10},
    animate: {opacity: 1, y: 0},
    exit: {opacity: 0, y: -10},
};

export const containerVariants = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.12,
            delayChildren: 0.1,
        },
    },
};

export const itemVariants = {
    hidden: {opacity: 0, y: 20, scale: 0.97},
    show: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {duration: 0.5, ease: [0.16, 1, 0.3, 1] as const},
    },
};

const headerContainerVariants = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.18,
            delayChildren: 0.15,
        },
    },
};

const headerItemVariants = {
    hidden: {opacity: 0, y: 18, scale: 0.99},
    show: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {duration: 0.9, ease: [0.16, 1, 0.3, 1] as const},
    },
};

const labelSpringConfig: Transition = {
    type: "spring",
    stiffness: 600,
    damping: 30,
};

export const StopWastingTime = (props: SVGMotionProps<SVGSVGElement>) => (
    <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={1003}
        height={1717}
        fill="none"
        viewBox="0 0 1003 1717"
        {...props}
    >
        <path
            fill="#4F46E5"
            d="M228.476 129.149h-96.368V99.573c0-13.803-1.232-22.593-3.697-26.372-2.465-3.78-6.572-5.67-12.323-5.67-6.244 0-11.009 2.548-14.295 7.641-3.122 5.094-4.683 12.817-4.683 23.168 0 13.309 1.807 23.332 5.422 30.069 3.451 6.737 13.227 14.87 29.33 24.4 46.171 27.44 75.254 49.951 87.249 67.532 11.994 17.581 17.992 45.925 17.992 85.031 0 28.426-3.369 49.376-10.105 62.849-6.573 13.474-19.389 24.811-38.449 34.013-19.06 9.037-41.242 13.555-66.546 13.555-27.769 0-51.512-5.258-71.229-15.774-19.553-10.516-32.37-23.907-38.449-40.174-6.08-16.267-9.119-39.352-9.119-69.257v-26.125h96.368v48.554c0 14.952 1.315 24.564 3.944 28.836 2.793 4.272 7.64 6.408 14.541 6.408 6.901 0 11.995-2.711 15.281-8.133 3.451-5.422 5.176-13.474 5.176-24.154 0-23.496-3.204-38.859-9.612-46.089-6.573-7.23-22.757-19.307-48.554-36.231-25.797-17.088-42.885-29.493-51.265-37.216-8.38-7.723-15.363-18.403-20.95-32.041C2.713 150.755.002 133.338.002 112.142c0-30.561 3.861-52.908 11.584-67.039 7.887-14.13 20.539-25.14 37.956-33.026C66.959 4.026 87.99 0 112.637 0c26.947 0 49.869 4.354 68.764 13.063 19.06 8.708 31.63 19.717 37.71 33.026 6.243 13.145 9.365 35.574 9.365 67.286v15.774ZM478.886 8.38v79.855H417.27V407.41H313.507V88.235h-61.37V8.38h226.749ZM739.648 242.03c0 40.092-.986 68.518-2.958 85.278-1.807 16.595-7.722 31.794-17.745 45.596-9.859 13.802-23.25 24.4-40.174 31.794-16.924 7.394-36.642 11.091-59.152 11.091-21.361 0-40.585-3.45-57.674-10.351-16.924-7.066-30.561-17.582-40.913-31.548-10.352-13.967-16.513-29.165-18.485-45.596-1.972-16.432-2.958-45.186-2.958-86.264v-68.271c0-40.092.904-68.436 2.712-85.031 1.971-16.76 7.887-32.04 17.745-45.843 10.023-13.802 23.497-24.4 40.421-31.794C577.391 3.697 597.108 0 619.619 0c21.36 0 40.502 3.533 57.426 10.598 17.089 6.901 30.809 17.335 41.16 31.301 10.352 13.967 16.514 29.166 18.485 45.597 1.972 16.431 2.958 45.185 2.958 86.263v68.271ZM635.885 110.91c0-18.567-1.068-30.398-3.204-35.491-1.971-5.258-6.161-7.887-12.569-7.887-5.423 0-9.613 2.136-12.57 6.408-2.794 4.108-4.19 16.431-4.19 36.97v186.329c0 23.168.904 37.463 2.711 42.885 1.972 5.422 6.408 8.133 13.309 8.133 7.066 0 11.584-3.122 13.556-9.365 1.972-6.244 2.957-21.114 2.957-44.611V110.91ZM778.589 8.38h104.502c28.262 0 49.951 2.218 65.067 6.654 15.281 4.437 26.701 10.845 34.259 19.225 7.723 8.38 12.899 18.567 15.528 30.562 2.795 11.83 4.185 30.233 4.185 55.208v34.752c0 25.468-2.624 44.035-7.882 55.702-5.258 11.666-14.953 20.621-29.083 26.864-13.967 6.244-32.287 9.366-54.962 9.366h-27.851v160.696H778.589V8.379ZM882.352 76.65v101.544c2.957.165 5.504.247 7.64.247 9.53 0 16.103-2.301 19.718-6.901 3.779-4.765 5.668-14.542 5.668-29.33v-32.78c0-13.638-2.136-22.51-6.408-26.618-4.272-4.108-13.145-6.162-26.618-6.162ZM224.011 459.052l-25.114 217.616h-70.64c-6.447-33.424-12.132-71.418-17.056-113.983-2.239 18.191-7.476 56.185-15.713 113.983H25.25L.002 459.052H54.93l5.775 75.944 5.909 73.255c2.06-37.904 7.252-87.638 15.578-149.199h58.823c.806 6.362 2.865 30.288 6.178 71.777l6.177 82.665c3.134-52.601 8.372-104.082 15.713-154.442h54.928ZM332.927 459.052l32.366 217.616H307.41l-2.82-39.114h-20.279l-3.357 39.114H222.4l28.739-217.616h81.788Zm-29.948 139.925c-2.866-24.643-5.731-55.065-8.596-91.267-5.73 41.578-9.311 72.001-10.743 91.267h19.339ZM494.756 524.915h-52.51v-16.13c0-7.527-.672-12.321-2.015-14.382s-3.581-3.092-6.715-3.092c-3.402 0-5.998 1.389-7.789 4.167-1.701 2.778-2.552 6.989-2.552 12.635 0 7.258.985 12.724 2.955 16.398 1.88 3.674 7.207 8.11 15.981 13.307 25.159 14.965 41.006 27.242 47.542 36.83 6.536 9.588 9.804 25.046 9.804 46.373 0 15.502-1.836 26.927-5.507 34.275-3.581 7.348-10.564 13.531-20.95 18.55-10.386 4.928-22.473 7.392-36.261 7.392-15.13 0-28.068-2.867-38.812-8.602-10.654-5.735-17.638-13.038-20.95-21.91-3.313-8.871-4.969-21.461-4.969-37.77v-14.248h52.51v26.48c0 8.154.717 13.396 2.149 15.726 1.522 2.33 4.163 3.495 7.924 3.495 3.76 0 6.536-1.479 8.326-4.436 1.88-2.957 2.82-7.348 2.82-13.172 0-12.815-1.745-21.193-5.237-25.136-3.581-3.943-12.4-10.529-26.457-19.759-14.056-9.319-23.368-16.085-27.934-20.296-4.566-4.212-8.371-10.037-11.415-17.474-2.955-7.438-4.432-16.936-4.432-28.496 0-16.667 2.104-28.854 6.312-36.561 4.297-7.706 11.191-13.71 20.682-18.011 9.49-4.391 20.95-6.586 34.38-6.586 14.683 0 27.173 2.374 37.469 7.124 10.386 4.749 17.235 10.753 20.548 18.011 3.402 7.169 5.103 19.4 5.103 36.695v8.603ZM631.203 459.052v43.55h-33.574v174.066h-56.54V502.602h-33.44v-43.55h123.554ZM700.635 459.052v217.616h-56.539V459.052h56.539ZM849.572 459.052v217.616h-49.556l-29.411-98.929v98.929h-47.273V459.052h47.273l31.694 97.988v-97.988h47.273ZM1002.13 539.163h-56.535v-19.759c0-12.456-.537-20.252-1.612-23.388-1.074-3.137-3.626-4.705-7.655-4.705-3.491 0-5.864 1.344-7.117 4.032-1.254 2.689-1.88 9.589-1.88 20.7v104.44c0 9.768.626 16.219 1.88 19.356 1.253 3.047 3.76 4.57 7.52 4.57 4.119 0 6.894-1.747 8.327-5.242 1.522-3.495 2.283-10.305 2.283-20.431v-25.808h-11.415v-33.066h66.204v116.806h-35.584l-5.238-15.592c-3.85 6.721-8.729 11.784-14.639 15.189-5.819 3.316-12.713 4.973-20.682 4.973-9.49 0-18.398-2.285-26.725-6.855-8.237-4.659-14.504-10.394-18.802-17.205-4.297-6.81-6.983-13.934-8.057-21.372-1.075-7.527-1.612-18.773-1.612-33.738v-64.653c0-20.789 1.119-35.888 3.357-45.297 2.239-9.409 8.64-18.012 19.205-25.808 10.654-7.886 24.398-11.828 41.23-11.828 16.563 0 30.306 3.405 41.229 10.215 10.923 6.81 18.041 14.92 21.354 24.329 3.314 9.319 4.964 22.895 4.964 40.728v9.409ZM242.368 719.931v86.55h-65.86v345.929H65.599V806.481H.002v-86.55h242.366ZM378.568 719.931v432.479H267.659V719.931h110.909ZM765.3 719.931v432.479h-96.946V860.441l-38.726 291.969H560.87l-40.834-285.291v285.291H423.09V719.931h143.575c4.215 26 8.694 56.631 13.436 91.892L595.38 921.88l25.291-201.949H765.3ZM809.822 719.931h184.936v86.55h-74.027v82.008h69.285v82.276h-69.285v95.095h81.399v86.55H809.822V719.931Z"
        />
        <path
            fill="#2D2B2B"
            d="M112.346 1348.43H67.134v-13.88c0-6.47-.578-10.6-1.734-12.37-1.157-1.77-3.084-2.66-5.782-2.66-2.93 0-5.165 1.19-6.707 3.58-1.464 2.39-2.197 6.02-2.197 10.87 0 6.25.848 10.95 2.544 14.11 1.62 3.16 6.206 6.98 13.76 11.45 21.662 12.87 35.307 23.43 40.934 31.68 5.628 8.25 8.442 21.55 8.442 39.89 0 13.34-1.581 23.17-4.741 29.49-3.084 6.32-9.097 11.64-18.039 15.96-8.942 4.24-19.35 6.36-31.22 6.36-13.029 0-24.168-2.47-33.418-7.4-9.174-4.94-15.187-11.22-18.04-18.85-2.851-7.63-4.278-18.46-4.278-32.49v-12.26h45.213v22.78c0 7.01.616 11.52 1.85 13.53 1.31 2 3.585 3 6.822 3 3.238 0 5.628-1.27 7.17-3.81 1.618-2.55 2.428-6.32 2.428-11.33 0-11.03-1.504-18.23-4.51-21.63-3.084-3.39-10.677-9.05-22.78-16.99-12.102-8.02-20.12-13.84-24.051-17.46-3.932-3.63-7.208-8.64-9.829-15.04-2.544-6.4-3.816-14.57-3.816-24.51 0-14.34 1.812-24.82 5.435-31.45 3.7-6.63 9.636-11.8 17.807-15.5 8.172-3.78 18.039-5.66 29.602-5.66 12.643 0 23.396 2.04 32.262 6.12 8.942 4.09 14.839 9.26 17.691 15.5 2.93 6.17 4.394 16.69 4.394 31.57v7.4ZM131.773 1291.77h81.174v37.46h-32.493v35.5h30.411v35.62h-30.411v41.16h35.73v37.47h-84.411v-187.21ZM328.348 1291.77v37.46H299.44v149.75h-48.682v-149.75h-28.792v-37.46h106.382ZM465.372 1291.77l27.867 187.21h-49.837l-2.428-33.65h-17.461l-2.891 33.65h-50.415l24.745-187.21h70.42Zm-25.786 120.37c-2.467-21.2-4.934-47.37-7.4-78.51-4.934 35.77-8.018 61.94-9.251 78.51h16.651ZM655.125 1360.68h-48.682v-16.99c0-10.72-.462-17.43-1.387-20.12-.925-2.7-3.122-4.05-6.591-4.05-3.007 0-5.049 1.16-6.129 3.47-1.079 2.31-1.618 8.25-1.618 17.81v89.84c0 8.4.539 13.96 1.618 16.65 1.08 2.62 3.238 3.93 6.476 3.93 3.546 0 5.936-1.5 7.169-4.5 1.311-3.01 1.966-8.87 1.966-17.58v-22.2h-9.829v-28.45h57.007v100.49h-30.643l-4.51-13.42c-3.314 5.78-7.516 10.14-12.603 13.07-5.011 2.85-10.947 4.28-17.808 4.28-8.171 0-15.841-1.97-23.011-5.9-7.092-4.01-12.488-8.94-16.188-14.8-3.7-5.86-6.013-11.99-6.938-18.39-.925-6.47-1.388-16.15-1.388-29.02v-55.62c0-17.88.964-30.87 2.891-38.97 1.927-8.09 7.439-15.49 16.536-22.2 9.173-6.78 21.006-10.17 35.499-10.17 14.261 0 26.094 2.93 35.499 8.78 9.405 5.86 15.533 12.84 18.385 20.93 2.853 8.02 4.279 19.7 4.279 35.04v8.09ZM785.327 1401.39c0 18.81-.463 32.14-1.388 40.01-.848 7.78-3.623 14.91-8.325 21.39-4.626 6.47-10.908 11.45-18.848 14.91-7.94 3.47-17.191 5.21-27.752 5.21-10.022 0-19.041-1.62-27.058-4.86-7.94-3.31-14.339-8.25-19.195-14.8-4.857-6.55-7.748-13.68-8.673-21.39-.925-7.71-1.387-21.2-1.387-40.47v-32.03c0-18.81.424-32.11 1.272-39.9.925-7.86 3.7-15.03 8.325-21.5 4.703-6.48 11.024-11.45 18.964-14.92 7.94-3.47 17.191-5.2 27.752-5.2 10.021 0 19.002 1.65 26.942 4.97 8.017 3.24 14.454 8.13 19.311 14.68 4.856 6.56 7.747 13.69 8.672 21.4.925 7.7 1.388 21.19 1.388 40.47v32.03Zm-48.681-61.52c0-8.71-.502-14.26-1.504-16.65-.925-2.47-2.89-3.7-5.897-3.7-2.544 0-4.51 1-5.897 3.01-1.311 1.92-1.966 7.7-1.966 17.34v87.42c0 10.87.424 17.57 1.272 20.12.925 2.54 3.006 3.81 6.244 3.81 3.315 0 5.435-1.46 6.36-4.39.925-2.93 1.388-9.91 1.388-20.93v-86.03ZM887.661 1291.77l27.868 187.21h-49.838l-2.428-33.65h-17.461l-2.89 33.65h-50.416l24.745-187.21h70.42Zm-25.786 120.37c-2.466-21.2-4.933-47.37-7.4-78.51-4.934 35.77-8.017 61.94-9.251 78.51h16.651ZM972.536 1291.77v149.74h29.604v37.47h-78.286v-187.21h48.682ZM113.133 1594.42h-48.7v-17c0-10.72-.463-17.43-1.389-20.13-.925-2.7-3.123-4.05-6.593-4.05-3.008 0-5.051 1.16-6.131 3.47-1.08 2.31-1.62 8.25-1.62 17.81v89.89c0 8.4.54 13.95 1.62 16.65 1.08 2.63 3.239 3.94 6.478 3.94 3.547 0 5.938-1.51 7.172-4.52 1.31-3 1.966-8.86 1.966-17.58v-22.21h-9.832v-28.46h57.029v100.53H82.478l-4.511-13.42c-3.316 5.78-7.52 10.14-12.609 13.07-5.013 2.86-10.95 4.28-17.814 4.28-8.175 0-15.848-1.96-23.02-5.9-7.095-4.01-12.494-8.94-16.195-14.8-3.702-5.87-6.015-12-6.94-18.4C.462 1671.11 0 1661.44 0 1648.56v-55.64c0-17.89.964-30.89 2.892-38.99 1.928-8.09 7.442-15.5 16.542-22.21 9.177-6.78 21.015-10.18 35.513-10.18 14.267 0 26.105 2.93 35.513 8.79 9.409 5.87 15.539 12.85 18.393 20.94 2.853 8.02 4.28 19.71 4.28 35.05v8.1ZM132.104 1525.48h81.206v37.48h-32.506v35.51h30.424v35.63h-30.424v41.18h35.745v37.48h-84.445v-187.28ZM328.756 1525.48v37.48h-28.919v149.8h-48.7v-149.8h-28.804v-37.48h106.423ZM465.835 1525.48l27.878 187.28h-49.857l-2.429-33.66h-17.468l-2.892 33.66h-50.435l24.755-187.28h70.448Zm-25.797 120.42c-2.467-21.21-4.935-47.39-7.403-78.55-4.936 35.78-8.02 61.97-9.254 78.55h16.657ZM543.917 1525.48h49.048c13.264 0 23.444 1.04 30.538 3.12 7.172 2.08 12.532 5.09 16.08 9.02 3.624 3.94 6.053 8.72 7.287 14.35 1.311 5.55 1.967 14.19 1.967 25.91v16.31c0 11.95-1.234 20.67-3.702 26.14-2.468 5.48-7.018 9.68-13.65 12.61-6.555 2.93-15.154 4.4-25.796 4.4h-13.072v75.42h-48.7v-187.28Zm48.7 32.04v47.66c1.389.08 2.584.11 3.586.11 4.473 0 7.558-1.08 9.255-3.23 1.773-2.24 2.66-6.83 2.66-13.77v-15.39c0-6.4-1.002-10.56-3.007-12.49-2.005-1.93-6.17-2.89-12.494-2.89ZM711.534 1525.48v149.8h29.614v37.48h-78.314v-187.28h48.7ZM837.16 1525.48l27.879 187.28h-49.857l-2.43-33.66h-17.467l-2.892 33.66h-50.435l24.755-187.28h70.447Zm-25.796 120.42c-2.468-21.21-4.935-47.39-7.403-78.55-4.936 35.78-8.02 61.97-9.254 78.55h16.657ZM982.105 1525.48v187.28H939.42l-25.334-85.14v85.14h-40.718v-187.28h40.718l27.3 84.33v-84.33h40.719Z"
        />
    </motion.svg>
);

const breakUpContainerVariants = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.18,
            delayChildren: 0.15,
        },
    },
};

const breakUpItemVariants = {
    hidden: {opacity: 0, y: 18, scale: 0.99},
    show: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {duration: 0.9, ease: [0.16, 1, 0.3, 1] as const},
    },
};

const BreakUp = (props: SVGMotionProps<SVGSVGElement>) => (
    <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={1099}
        height={1475}
        fill="none"
        viewBox="0 0 1099 1475"
        initial="hidden"
        animate="show"
        {...props}
    >
        <motion.g
            style={{transformOrigin: "50% 50%", transformBox: "fill-box"}}
            animate={{y: [0, -10, 0], rotate: [0, -0.6, 0.6, 0]}}
            transition={{duration: 7, repeat: Infinity, ease: "easeInOut"}}
        >
            <motion.g variants={breakUpContainerVariants}>
                <motion.g variants={breakUpItemVariants}>
                    <path
                        fill="#4F46E5"
                        d="M0 0h89.515c28.276 0 49.66 2.202 64.153 6.607 14.635 4.405 26.428 13.356 35.38 26.855 8.951 13.356 13.427 34.953 13.427 64.792 0 20.176-3.197 34.243-9.591 42.2-6.252 7.957-18.685 14.066-37.298 18.329 20.745 4.689 34.811 12.504 42.2 23.444 7.389 10.799 11.083 27.423 11.083 49.873v31.97c0 23.302-2.7 40.566-8.099 51.791-5.257 11.225-13.712 18.897-25.363 23.018-11.651 4.12-35.522 6.181-71.612 6.181H0V0Zm89.728 59.037v76.728c3.837-.142 6.82-.213 8.952-.213 8.809 0 14.422-2.132 16.837-6.394 2.416-4.405 3.623-16.838 3.623-37.298 0-10.799-.994-18.33-2.983-22.592-1.99-4.405-4.618-7.176-7.886-8.312-3.126-1.137-9.307-1.777-18.543-1.919Zm0 130.437v96.548c12.646-.426 20.674-2.415 24.084-5.967 3.552-3.553 5.328-12.291 5.328-26.215v-32.183c0-14.777-1.563-23.729-4.688-26.855-3.126-3.126-11.367-4.902-24.724-5.328ZM241.265 0h63.513c42.342 0 70.973 1.634 85.892 4.902 15.061 3.268 27.281 11.651 36.658 25.15 9.52 13.356 14.28 34.74 14.28 64.152 0 26.855-3.339 44.9-10.017 54.135-6.678 9.236-19.821 14.778-39.429 16.625 17.761 4.404 29.696 10.301 35.806 17.69 6.11 7.388 9.875 14.208 11.296 20.46 1.563 6.11 2.344 23.089 2.344 50.939v91.007h-83.334V230.395c0-18.471-1.492-29.909-4.476-34.314-2.842-4.405-10.443-6.607-22.805-6.607V345.06h-89.728V0Zm89.728 59.037v76.728c10.088 0 17.122-1.35 21.1-4.05 4.121-2.842 6.181-11.864 6.181-27.067v-18.97c0-10.94-1.989-18.115-5.968-21.525-3.836-3.41-10.941-5.116-21.313-5.116ZM476.562 0H626.18v69.055h-59.89v65.431h56.054v65.644H566.29v75.875h65.858v69.055H476.562V0ZM813.096 0l51.365 345.06h-91.86l-4.475-62.022h-32.183l-5.329 62.022h-92.925L683.299 0h129.797Zm-47.528 221.87c-4.547-39.074-9.094-87.313-13.64-144.716-9.094 65.928-14.777 114.167-17.051 144.716h30.691ZM1093.58 0l-51.37 155.799 56.27 189.261h-92.71l-36.235-147.913V345.06h-89.729V0h89.729v134.06L1009.39 0h84.19ZM520.332 375.921v584.631c0 66.278-2.161 112.928-6.484 139.948-4.322 26.65-17.11 54.21-38.363 82.67-21.252 28.09-49.349 49.53-84.29 64.29-34.581 14.41-75.465 21.62-122.654 21.62-52.231 0-98.339-8.65-138.323-25.94-39.984-17.29-69.882-39.8-89.694-67.54-19.812-27.74-31.519-56.91-35.12-87.53C1.8 1057.09 0 992.251 0 893.552V375.921h227.477v655.959c0 38.18 1.981 62.67 5.943 73.48 4.323 10.45 12.788 15.67 25.395 15.67 14.409 0 23.595-5.76 27.557-17.29 4.322-11.89 6.484-39.63 6.484-83.21V375.921h227.476ZM608.405 375.921h229.098c61.957 0 109.505 4.863 142.645 14.589 33.502 9.726 58.532 23.774 75.102 42.145 16.93 18.371 28.28 40.705 34.04 67.001 6.13 25.935 9.19 66.279 9.19 121.032v76.186c0 55.834-5.76 96.538-17.29 122.113-11.53 25.576-32.78 45.207-63.76 58.896-30.618 13.688-70.782 20.532-120.492 20.532h-61.056v149.295H608.405V375.921Zm227.477 149.67v222.614c6.484.36 12.067.54 16.75.54 20.892 0 35.301-5.043 43.226-15.129 8.285-10.446 12.427-31.879 12.427-64.299v-71.863c0-29.898-4.683-49.349-14.048-58.355-9.366-9.005-28.818-13.508-58.355-13.508Z"
                    />
                </motion.g>
                <motion.path
                    fill="#2D2B2B"
                    d="m799.924 1064.95-21.525 186.35h-60.546c-5.526-28.62-10.398-61.16-14.619-97.61-1.918 15.58-6.408 48.12-13.468 97.61h-60.201l-21.64-186.35h47.079l4.95 65.03 5.064 62.74c1.765-32.47 6.216-75.05 13.353-127.77h50.417c.69 5.44 2.455 25.93 5.295 61.46l5.295 70.79c2.685-45.04 7.175-89.13 13.467-132.25h47.079ZM858.053 1064.95v186.35h-48.46v-186.35h48.46ZM975.117 1064.95v37.29H946.34v149.06h-48.46v-149.06h-28.662v-37.29h105.899ZM1097.59 1064.95v186.35h-48.46v-78.27h-14.5v78.27h-48.463v-186.35h48.463v66.64h14.5v-66.64h48.46ZM122.852 1318.58v152.67H88.049v-103.07l-13.903 103.07H49.462l-14.659-100.71v100.71H0v-152.67h51.543c1.513 9.17 3.12 19.99 4.823 32.44l5.486 38.85 9.079-71.29h51.921ZM223.668 1318.58l-29.034 97.41v55.26H157.75v-55.26l-27.994-97.41h36.6c5.737 29.73 8.953 49.76 9.647 60.07 2.206-16.29 5.895-36.31 11.065-60.07h36.6ZM230.572 1318.58h67.431v30.55h-27.615v28.95h24.589v29.05h-24.589v64.12h-39.816v-152.67ZM347.56 1318.58v152.67h-39.816v-152.67h39.816ZM443.742 1318.58v30.55h-23.643v122.12h-39.816v-122.12h-23.549v-30.55h87.008ZM541.721 1318.58v152.67h-34.898l-20.712-69.4v69.4h-33.29v-152.67h33.29l22.32 68.74v-68.74h33.29ZM557.799 1318.58h66.391v30.55h-26.576v28.95h24.873v29.05h-24.873v33.57h29.224v30.55h-69.039v-152.67ZM722.736 1364.78h-36.979v-11.31c0-5.28-.472-8.65-1.418-10.09-.946-1.45-2.522-2.17-4.729-2.17-2.396 0-4.224.97-5.485 2.92-1.198 1.95-1.797 4.91-1.797 8.87 0 5.09.693 8.92 2.081 11.5 1.324 2.58 5.075 5.69 11.254 9.34 17.717 10.5 28.877 19.11 33.479 25.84 4.603 6.72 6.904 17.57 6.904 32.53 0 10.88-1.292 18.89-3.877 24.05-2.522 5.15-7.44 9.49-14.754 13.01-7.314 3.46-15.825 5.19-25.535 5.19-10.655 0-19.766-2.01-27.332-6.04-7.503-4.02-12.421-9.14-14.754-15.37-2.332-6.22-3.499-15.05-3.499-26.5v-9.99h36.979v18.57c0 5.73.504 9.4 1.513 11.04 1.072 1.63 2.932 2.45 5.58 2.45s4.602-1.04 5.863-3.11c1.324-2.08 1.986-5.16 1.986-9.24 0-8.99-1.229-14.87-3.688-17.64-2.522-2.76-8.732-7.39-18.631-13.86-9.899-6.54-16.456-11.29-19.672-14.24-3.215-2.96-5.895-7.04-8.038-12.26-2.081-5.22-3.121-11.88-3.121-19.99 0-11.7 1.481-20.25 4.445-25.65 3.026-5.41 7.881-9.62 14.564-12.64 6.683-3.08 14.754-4.62 24.211-4.62 10.34 0 19.136 1.67 26.386 5 7.314 3.33 12.137 7.54 14.47 12.63 2.396 5.03 3.594 13.62 3.594 25.75v6.03ZM822.985 1364.78h-36.979v-11.31c0-5.28-.473-8.65-1.418-10.09-.946-1.45-2.522-2.17-4.729-2.17-2.396 0-4.225.97-5.485 2.92-1.198 1.95-1.797 4.91-1.797 8.87 0 5.09.693 8.92 2.08 11.5 1.324 2.58 5.076 5.69 11.255 9.34 17.716 10.5 28.876 19.11 33.479 25.84 4.602 6.72 6.904 17.57 6.904 32.53 0 10.88-1.293 18.89-3.878 24.05-2.522 5.15-7.44 9.49-14.753 13.01-7.314 3.46-15.826 5.19-25.535 5.19-10.656 0-19.766-2.01-27.332-6.04-7.503-4.02-12.421-9.14-14.754-15.37-2.333-6.22-3.499-15.05-3.499-26.5v-9.99h36.978v18.57c0 5.73.505 9.4 1.513 11.04 1.072 1.63 2.932 2.45 5.58 2.45s4.603-1.04 5.864-3.11c1.324-2.08 1.986-5.16 1.986-9.24 0-8.99-1.229-14.87-3.688-17.64-2.522-2.76-8.733-7.39-18.631-13.86-9.899-6.54-16.456-11.29-19.672-14.24-3.215-2.96-5.895-7.04-8.039-12.26-2.08-5.22-3.121-11.88-3.121-19.99 0-11.7 1.482-20.25 4.445-25.65 3.027-5.41 7.882-9.62 14.565-12.64 6.683-3.08 14.753-4.62 24.211-4.62 10.34 0 19.135 1.67 26.386 5 7.314 3.33 12.137 7.54 14.47 12.63 2.396 5.03 3.594 13.62 3.594 25.75v6.03ZM838.873 1318.58h40.1c10.844 0 19.167.84 24.967 2.54 5.864 1.7 10.246 4.15 13.146 7.36 2.963 3.2 4.949 7.1 5.958 11.69 1.072 4.53 1.608 11.57 1.608 21.13v13.29c0 9.75-1.009 16.85-3.027 21.31-2.017 4.47-5.737 7.89-11.159 10.28-5.359 2.39-12.389 3.59-21.09 3.59h-10.687v61.48h-39.816v-152.67Zm39.816 26.12v38.85c1.135.06 2.112.1 2.932.1 3.657 0 6.178-.89 7.566-2.65 1.45-1.82 2.175-5.56 2.175-11.22v-12.54c0-5.22-.82-8.61-2.459-10.18-1.639-1.58-5.044-2.36-10.214-2.36ZM1004.85 1318.58l22.79 152.67h-40.758l-1.986-27.44h-14.281l-2.364 27.44h-41.235l20.239-152.67h57.595Zm-21.089 98.17c-2.018-17.29-4.035-38.64-6.053-64.04-4.035 29.17-6.557 50.52-7.566 64.04h13.619ZM1074.27 1318.58v122.12h24.21v30.55h-64.03v-152.67h39.82Z"
                    variants={breakUpItemVariants}
                />
            </motion.g>
        </motion.g>
    </motion.svg>
);

export function AnimatedButton({
    label,
    icon: Icon,
    iconPosition = "left",
    animateIcon = false,
    onClick,
    isSubmitting,
    disabled,
}: {
    label: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    iconPosition?: "left" | "right";
    animateIcon?: boolean;
    onClick: () => void | Promise<void>;
    isSubmitting?: boolean;
    disabled?: boolean;
}) {
    const [buttonWidth, setButtonWidth] = React.useState<number | undefined>(undefined);
    const [isLoading, setIsLoading] = React.useState(false);
    const [committed, setCommitted] = React.useState({
        label,
        Icon,
        iconPosition,
        animateIcon,
    });
    const measureRef = React.useRef<HTMLDivElement>(null);
    const currentRef = React.useRef({label, Icon, iconPosition, animateIcon});
    currentRef.current = {label, Icon, iconPosition, animateIcon};

    React.useLayoutEffect(() => {
        if (!isLoading && isSubmitting !== true && label !== committed.label) {
            setCommitted({label, Icon, iconPosition, animateIcon});
        }
    }, [label, isLoading, isSubmitting, committed.label, committed.Icon]);

    const CommittedIcon = committed.Icon;
    const committedIconPosition = committed.iconPosition;
    const committedAnimateIcon = committed.animateIcon;

    React.useEffect(() => {
        if (measureRef.current != null) {
            const {width} = measureRef.current.getBoundingClientRect();
            setButtonWidth(width);
        }
    }, [committed.label, committed.Icon]);

    const handleClick = async () => {
        if (disabled === true) return;
        void hardware.haptic();
        setIsLoading(true);
        try {
            await onClick();
        } finally {
            setCommitted(currentRef.current);
            setIsLoading(false);
        }
    };

    return (
        <>
            <div
                ref={measureRef}
                className="flex items-center gap-1.5 px-6 py-3 font-medium"
                style={{
                    position: "absolute",
                    visibility: "hidden",
                    whiteSpace: "nowrap",
                }}
            >
                {committedIconPosition === "left" && (
                    <CommittedIcon className="size-5" />
                )}
                {committed.label}
                {committedIconPosition === "right" && (
                    <CommittedIcon className="size-5" />
                )}
            </div>

            <motion.div
                className="flex justify-center"
                animate={{width: buttonWidth}}
                transition={labelSpringConfig}
            >
                <typography.AsyncButton
                    className={classNames(
                        "text-white px-6 py-3 rounded-3xl font-medium w-full overflow-hidden transition-colors",
                        disabled === true ? "bg-stone-300/80" : "bg-indigo-600",
                    )}
                    onClick={handleClick}
                    forceLoading={isLoading || isSubmitting}
                    disabled={disabled}
                >
                    <AnimatePresence mode="sync" initial={false}>
                        <motion.span
                            key={committed.label}
                            className="flex items-center gap-1.5"
                            style={{whiteSpace: "nowrap"}}
                            initial={{
                                y: -20,
                                opacity: 0,
                                filter: "blur(10px)",
                                position: "absolute",
                            }}
                            animate={{
                                y: 0,
                                opacity: 1,
                                filter: "blur(0px)",
                                position: "relative",
                            }}
                            exit={{
                                y: 20,
                                opacity: 0,
                                filter: "blur(10px)",
                                position: "absolute",
                            }}
                            transition={{
                                duration: 0.2,
                                ease: "easeInOut",
                            }}
                        >
                            <style>{nudgeKeyframes}</style>
                            {committedIconPosition === "left" &&
                                (committedAnimateIcon === true && disabled !== true ? (
                                    <span
                                        className="inline-block"
                                        style={{
                                            animation:
                                                "nudge-x 1.2s ease-in-out infinite",
                                        }}
                                    >
                                        <CommittedIcon className="size-5" />
                                    </span>
                                ) : (
                                    <CommittedIcon className="size-5" />
                                ))}
                            {committed.label}
                            {committedIconPosition === "right" &&
                                (committedAnimateIcon === true && disabled !== true ? (
                                    <span
                                        className="inline-block"
                                        style={{
                                            animation:
                                                "nudge-x 1.2s ease-in-out infinite",
                                        }}
                                    >
                                        <CommittedIcon className="size-5" />
                                    </span>
                                ) : (
                                    <CommittedIcon className="size-5" />
                                ))}
                        </motion.span>
                    </AnimatePresence>
                </typography.AsyncButton>
            </motion.div>
        </>
    );
}

function ProgressDots({current, total}: {current: number; total: number}) {
    return (
        <div className="flex justify-center gap-2">
            {Array.from({length: total}).map((_, i) => (
                <motion.div
                    key={i}
                    layout
                    className={classNames(
                        "h-2 rounded-full",
                        i === current ? "w-4 bg-indigo-600" : "w-2 bg-gray-300",
                    )}
                    transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 35,
                    }}
                />
            ))}
        </div>
    );
}

function CheckboxOption({
    label,
    checked,
    onChange,
    subtitle,
}: {
    label: string;
    checked: boolean;
    onChange: () => void;
    subtitle?: string;
}) {
    return (
        <button
            onClick={() => {
                void hardware.haptic();
                onChange();
            }}
            style={
                checked
                    ? {
                          borderColor: "#4F46E5",
                          backgroundColor: "rgba(79, 70, 229, 0.05)",
                      }
                    : {}
            }
            className={classNames(
                "w-full px-4 py-3.5 rounded-xl border-2 flex items-center gap-2 transition-all",
                checked ? "" : "border-stone-200 bg-white",
            )}
        >
            <div
                style={
                    checked
                        ? {
                              backgroundColor: "#4F46E5",
                          }
                        : {}
                }
                className={classNames(
                    "size-6 rounded-md flex items-center justify-center shrink-0",
                    checked ? "" : "border-2 border-gray-300",
                )}
            >
                {checked && <CheckIcon className="size-4 text-white" strokeWidth={3} />}
            </div>
            <div className="text-left">
                <div className="text-[15px] font-medium text-gray-900">{label}</div>
                {subtitle != null && (
                    <div className="text-xs text-gray-400">{subtitle}</div>
                )}
            </div>
        </button>
    );
}

function HelperText({children}: {children: React.ReactNode}) {
    return <p className="mt-3 text-center text-sm text-gray-600">{children}</p>;
}

function ExpectationRow({text}: {text: string}) {
    return (
        <div className="flex items-center gap-2.5">
            <div className="flex size-8 items-center justify-center rounded-full bg-green-100">
                <CheckIcon className="size-4 text-green-500" strokeWidth={3} />
            </div>
            <span className="text-base text-gray-800">{text}</span>
        </div>
    );
}

function SummaryRow({
    icon,
    label,
    value,
}: {
    icon: string;
    label: string;
    value: string | null;
}) {
    return (
        <div className="mb-3.5 flex items-center">
            <span className="mr-2.5 text-base">{icon}</span>
            <span className="flex-1 text-sm text-gray-600">{label}</span>
            <span className="text-sm font-semibold text-gray-900 capitalize">
                {value}
            </span>
        </div>
    );
}

function MiniTag({children}: {children: React.ReactNode}) {
    return (
        <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-600">
            {children}
        </span>
    );
}

type Route =
    | "start"
    | "mode"
    | "observe"
    | "reward"
    | "email"
    | "profile"
    | "goal"
    | "framing"
    | "cooking"
    | "assembly"
    | "equipment"
    | "repetition"
    | "eating_modes"
    | "nights_out"
    | "expectation"
    | "confirmation"
    | MFPRoute;

type OnboardingValues = {
    mode: "observe" | "plan" | "track" | "mfp" | null;
    goal: "lose" | "maintain" | "gain" | null;
    cookingFrequency: "never" | "occasionally" | "often" | null;
    assemblyWilling: "yes" | "no" | null;
    repetitionTolerance: "none" | "few" | "week" | null;
    nightsOut: "0" | "1" | "2" | "3+" | null;
};

type OnboardingArrays = {
    equipment: string[];
    eatingModes: string[];
};

// Internal config type - what each route case returns
type RouteConfig = {
    animation: React.ReactNode | null;
    content: React.ReactNode;
    footer?: React.ReactNode;
    isSubmitting?: boolean;
    next: {
        label: string;
        disabled?: boolean;
        // Only specify if different from default navigation
        onNext?: () => void | Promise<void>;
        // These rarely change, so optional with defaults
        icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
        iconPosition?: "left" | "right";
        animateIcon?: boolean;
    };
};

// Final return type - fully computed, ready to render
type OnboardingScreen = {
    route: Route;
    animation: React.ReactNode | null;
    content: React.ReactNode;
    footer?: React.ReactNode;
    isSubmitting?: boolean;
    back: (() => void) | null;
    next: {
        onNext: () => void | Promise<void>;
        label: string;
        icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
        iconPosition: "left" | "right";
        animateIcon: boolean;
        disabled: boolean;
    };
    progress: {
        current: number | null;
        total: number;
    };
};

type FlowRoute =
    | Route
    | {route: Route; skipWhen: (values: OnboardingValues) => boolean};

const MODE_FLOWS: Record<"observe" | "plan" | "track" | "mfp", FlowRoute[]> = {
    observe: ["start", "mode", "observe", "reward", "email"],
    plan: [
        "start",
        "mode",
        "profile",
        "goal",
        "framing",
        "cooking",
        {route: "assembly", skipWhen: (v) => v.cookingFrequency !== "never"},
        "equipment",
        "repetition",
        "eating_modes",
        "nights_out",
        "expectation",
        "email",
    ],
    track: ["start", "mode", "email"],
    mfp: [
        "start",
        "mode",
        "mfp_promise",
        "mfp_credentials",
        "mfp_importing",
        "mfp_pitch",
        "mfp_confirm",
    ],
};

function useFlowNavigation(
    mode: "observe" | "plan" | "track" | "mfp",
    currentRoute: Route,
    values: OnboardingValues,
) {
    const activeRoutes = React.useMemo(() => {
        return MODE_FLOWS[mode]
            .filter((r) => typeof r === "string" || !r.skipWhen(values))
            .map((r) => (typeof r === "string" ? r : r.route));
    }, [mode, values]);

    const currentIndex = activeRoutes.indexOf(currentRoute);

    return {
        // null for "start" (index 0), otherwise 0-indexed progress
        progressIndex: currentIndex === 0 ? null : currentIndex - 1,
        // Total progress steps (excluding start)
        totalProgress: activeRoutes.length - 1,
        // Previous route, or null if at start
        back: currentIndex > 0 ? activeRoutes[currentIndex - 1] : null,
        // Next route, or null if at end
        next:
            currentIndex < activeRoutes.length - 1
                ? activeRoutes[currentIndex + 1]
                : null,
    };
}

function useOnboarding(props: {joyTag: string | null}): OnboardingScreen {
    const ids = React.useId();
    const dispatch = useAppDispatch();
    const [route, _setRoute] = React.useState<Route>("start");
    const platform = useAppSelector((state) => state.boot.platform);
    const subscriptionDollarsPerMonth = platform === "ios" ? "4.99" : "5";

    const setRoute = (nextRoute: Route, direction: "next" | "back") => {
        const event =
            direction === "next" ? "PLAN_ONBOARDING_NEXT" : "PLAN_ONBOARDING_BACK";
        void analytics.track(event, {
            from_route: route,
            to_route: nextRoute,
        });
        _setRoute(nextRoute);
    };
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [values, _setValues] = React.useState<OnboardingValues>({
        mode:
            props.joyTag == "mfp" || props.joyTag?.startsWith("mfp-") == true
                ? "mfp"
                : "observe",
        goal: null,
        cookingFrequency: null,
        assemblyWilling: null,
        repetitionTolerance: null,
        nightsOut: null,
    });

    const setValues: typeof _setValues = (updater) => {
        _setValues((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;

            if (next.mode !== prev.mode && next.mode != null) {
                void analytics.track("PLAN_ONBOARDING_MODE_SELECTED", {
                    onboarding_mode: next.mode,
                });
            }
            if (next.goal !== prev.goal && next.goal != null) {
                void analytics.track("PLAN_ONBOARDING_GOAL_SELECTED", {
                    onboarding_goal: next.goal,
                });
            }

            return next;
        });
    };

    const [arrayValues, setArrayValues] = React.useState<OnboardingArrays>({
        equipment: ["microwave"],
        eatingModes: [],
    });

    const onboardingForm = forms.useForm(
        {
            fields: {
                mode: {
                    kind: "select",
                    nullable: true,
                    options: [
                        ["observe", "Observe first"],
                        ["plan", "Plan now"],
                        ["mfp", "Migrate from MyFitnessPal"],
                        ["track", "Just track"],
                    ],
                },
                goal: {
                    kind: "select",
                    nullable: true,
                    options: [
                        ["lose", "Lose fat"],
                        ["maintain", "Maintain"],
                        ["gain", "Gain muscle"],
                    ],
                },
                cookingFrequency: {
                    kind: "select",
                    nullable: true,
                    options: [
                        ["never", "Never"],
                        ["occasionally", "Occasionally"],
                        ["often", "Often"],
                    ],
                },
                assemblyWilling: {
                    kind: "select",
                    nullable: true,
                    options: [
                        ["yes", "Yes"],
                        ["no", "No"],
                    ],
                },
                repetitionTolerance: {
                    kind: "select",
                    nullable: true,
                    options: [
                        ["none", "No, I need variety"],
                        ["few", "Yes, for a few days"],
                        ["week", "Yes, all week"],
                    ],
                },
                nightsOut: {
                    kind: "select",
                    nullable: true,
                    options: [
                        ["0", "0"],
                        ["1", "1"],
                        ["2", "2"],
                        ["3+", "3+"],
                    ],
                },
            },
        },
        {value: values, onChange: setValues},
    );

    const [emailValues, setEmailValues] = React.useState({email: ""});
    const emailForm = forms.useForm(
        {
            fields: {
                email: {
                    kind: "email",
                    placeholder: "Email address",
                },
            },
        },
        {value: emailValues, onChange: setEmailValues},
    );

    const profileHook = profile.useProfile();

    const isProfileComplete = profileHook.isComplete;

    const flowNav = useFlowNavigation(values.mode ?? "observe", route, values);

    const mfpFlow = useMFPFlow({
        route: (route.startsWith("mfp_") ? route : "mfp_promise") as MFPRoute,
        setRoute: (r) => setRoute(r, "next"),
    });

    // Default navigation behavior - used by most routes
    const defaultOnNext = () => {
        void hardware.haptic();
        if (flowNav.next != null) {
            setRoute(flowNav.next, "next");
        }
    };

    // Computed back handler (mfp_why is not in the flow, so override back)
    const goBack =
        route === "mfp_why"
            ? () => setRoute("mfp_credentials", "back")
            : flowNav.back != null
              ? () => setRoute(flowNav.back!, "back")
              : null;

    const toggleArrayItem = (key: keyof OnboardingArrays, item: string) => {
        setArrayValues((prev) => ({
            ...prev,
            [key]: prev[key].includes(item)
                ? prev[key].filter((x) => x !== item)
                : [...prev[key], item],
        }));
    };

    const handleLegacyRegistration = async (email: string) => {
        setIsSubmitting(true);
        const {platform} = await hardware.getAppInfo();
        const tag = await hardware.getTag();

        void analytics.track("REGISTERING", {registration_method: "app"});

        const response = await server.core.rpc.register({
            email,
            with_notification: false,
            invite_code: "app",
            recipe_uuid: null,
            source: tag ?? "app",
            platform,
            timezone: dates.deviceTimezone(),
        });

        if (response.type === "success") {
            const profile = response.data;
            void analytics.registered(profile.user.tracking_key, "app", "trial");
            await dispatch(auth.postLogin(profile, true));
            void router.push("base.index", {});
        } else {
            setIsSubmitting(false);
            alert("Error registering");
        }
    };

    const handleLearningWeekRegistration = async (email: string) => {
        setIsSubmitting(true);
        const {platform} = await hardware.getAppInfo();

        void analytics.track("REGISTERING", {registration_method: "learning_week"});

        const response = await server.core.rpc.learning_week_registration({
            registration: {
                email,
                with_notification: false,
                invite_code: "learning_week",
                recipe_uuid: null,
                source: null,
                platform,
            },
        });

        if (response.type === "success") {
            const profile = response.data;
            void analytics.registered(
                profile.user.tracking_key,
                "learning_week",
                "trial",
            );
            await dispatch(auth.postLogin(profile, true));
            void router.push("today.journal", {});
        } else {
            setIsSubmitting(false);
            alert("Error registering");
            throw new Error("Error registering");
        }
    };

    const handlePlanRegistration = async (email: string) => {
        const formValues = onboardingForm.values;
        const profileValues = profileHook.values;

        if (
            formValues.goal == null ||
            profileValues.gender == null ||
            formValues.cookingFrequency == null ||
            formValues.assemblyWilling == null ||
            formValues.repetitionTolerance == null ||
            formValues.nightsOut == null ||
            profileValues.date_of_birth == null
        ) {
            throw new Error("Missing required fields");
        }

        setIsSubmitting(true);
        const {platform} = await hardware.getAppInfo();

        void analytics.track("REGISTERING", {registration_method: "plan_waitlist"});

        const response = await server.core.rpc.plan_registration({
            registration: {
                email,
                with_notification: false,
                invite_code: "plan_waitlist",
                recipe_uuid: null,
                source: null,
                platform,
            },
            date_of_birth: profileValues.date_of_birth,
            gender: profileValues.gender,
            units: profileValues.units,
            feet: profileValues.feet,
            inches: profileValues.inches,
            heightCm: profileValues.heightCm,
            weightLb: profileValues.weightLb,
            weightKg: profileValues.weightKg,
            goal: formValues.goal,
            cookingFrequency: formValues.cookingFrequency,
            assemblyWilling: formValues.assemblyWilling,
            repetitionTolerance: formValues.repetitionTolerance,
            nightsOut: formValues.nightsOut,
        });

        if (response.type === "success") {
            const profileData = response.data;
            void analytics.registered(
                profileData.user.tracking_key,
                "plan_waitlist",
                "trial",
            );
            await dispatch(auth.postLogin(profileData, true));
            void router.push("today.journal", {});
        } else {
            setIsSubmitting(false);
            alert("Error registering");
            throw new Error("Error registering");
        }
    };

    const tasksAnimationElement = (
        <Lottie
            animationData={tasksAnimation}
            loop={true}
            className="size-40 rounded-full bg-indigo-100 p-4 *:opacity-70"
        />
    );

    const config: RouteConfig = match(route)
        .with("mode", () => ({
            animation: (
                <Lottie
                    animationData={decisionAnimation}
                    loop={true}
                    className="size-40 rounded-full bg-indigo-100 pl-1 *:scale-125"
                />
            ),
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center"
                    variants={headerContainerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        id={`${ids}-mode`}
                        className="mb-2 text-center text-4xl font-light text-pretty"
                        variants={headerItemVariants}
                    >
                        Pick your path
                    </motion.h1>
                    <motion.p
                        id={`${ids}-mode-help`}
                        className="mb-8 text-center text-lg text-gray-600"
                        variants={headerItemVariants}
                    >
                        You can change this anytime.
                    </motion.p>

                    <motion.div variants={headerItemVariants}>
                        <forms.Widget
                            field={onboardingForm.fields.mode}
                            component={profile.BigRadioSelect}
                            props={{
                                "aria-labelledby": `${ids}-mode`,
                                "aria-describedby": `${ids}-mode-help`,
                                descriptions: {
                                    observe: "Learn your patterns before changes",
                                    plan: "Jump into structured meal planning",
                                    mfp: "Bring your MyFitnessPal data to Joy",
                                    track: false,
                                },
                                extras: {
                                    observe: (
                                        <span className="rounded-lg bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                                            RECOMMENDED
                                        </span>
                                    ),
                                },
                            }}
                        />
                    </motion.div>
                </motion.div>
            ),
            footer: (
                <button
                    onClick={() => {
                        void hardware.haptic();
                        onboardingForm.fields.mode.onChange("track");
                        setRoute("email", "next");
                    }}
                    className="text-base"
                >
                    I just want to track
                </button>
            ),
            next: {
                label: "Continue",
                disabled: onboardingForm.values.mode == null,
            },
        }))
        .with("profile", () => ({
            animation: null,
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-between py-8"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        className="text-4xl font-extralight"
                        variants={itemVariants}
                    >
                        Your details
                    </motion.h1>
                    <motion.p
                        className="text-xl font-extralight"
                        variants={itemVariants}
                    >
                        Tell us a little about yourself.
                    </motion.p>

                    {profileHook.component}
                </motion.div>
            ),
            next: {
                label: "Continue",
                disabled: !isProfileComplete,
            },
        }))
        .with("observe", () => ({
            animation: (
                <Lottie
                    animationData={cameraAnimation}
                    loop={true}
                    className="size-40 rounded-full bg-indigo-100 p-2 *:opacity-80"
                />
            ),
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center gap-6"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        className="text-center text-4xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        Here's what you'll do
                    </motion.h1>
                    {[
                        {
                            text: "Take a picture of what you eat",
                            Icon: CameraIcon,
                            bg: "bg-rose-100",
                            color: "text-rose-600",
                        },
                        {
                            text: "Weigh yourself once a day",
                            Icon: ScaleIcon,
                            bg: "bg-sky-100",
                            color: "text-sky-600",
                        },
                        {
                            text: "Do this for the next 7 days",
                            Icon: CalendarDaysIcon,
                            bg: "bg-amber-100",
                            color: "text-amber-600",
                        },
                    ].map(({text, Icon, bg, color}, index) => (
                        <motion.div
                            key={index}
                            className="flex items-center gap-3 rounded-2xl bg-white/50 p-4"
                            variants={itemVariants}
                        >
                            <div
                                className={classNames(
                                    "size-10 rounded-full flex items-center justify-center shrink-0",
                                    bg,
                                )}
                            >
                                <Icon className={classNames("size-5", color)} />
                            </div>
                            <p className="text-lg font-normal text-gray-800">{text}</p>
                        </motion.div>
                    ))}
                    <motion.p
                        className="text-center text-2xl text-gray-800"
                        variants={itemVariants}
                    >
                        That's it. We'll do the rest.
                    </motion.p>
                </motion.div>
            ),
            next: {
                label: "What you get",
            },
        }))
        .with("reward", () => ({
            animation: tasksAnimationElement,
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center gap-6"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        className="text-center text-4xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        Here's what you get
                    </motion.h1>
                    {[
                        {
                            title: "A flexible plan, not strict rules",
                            subtitle: "Swap meals. Dine out.",
                            Icon: AdjustmentsHorizontalIcon,
                            bg: "bg-violet-100",
                            color: "text-violet-600",
                        },
                        {
                            title: "Personalized guidelines",
                            subtitle: "Not generic formulas.",
                            Icon: ChartBarIcon,
                            bg: "bg-emerald-100",
                            color: "text-emerald-600",
                        },
                        {
                            title: "Auto-generated grocery lists",
                            subtitle: "When cooking makes sense.",
                            Icon: ShoppingCartIcon,
                            bg: "bg-orange-100",
                            color: "text-orange-600",
                        },
                    ].map(({title, subtitle, Icon, bg, color}, index) => (
                        <motion.div
                            key={index}
                            className="flex items-start gap-3 rounded-2xl bg-white/50 p-4"
                            variants={itemVariants}
                        >
                            <div
                                className={classNames(
                                    "size-10 rounded-full flex items-center justify-center shrink-0",
                                    bg,
                                )}
                            >
                                <Icon className={classNames("size-5", color)} />
                            </div>
                            <div>
                                <p className="text-lg font-medium text-gray-800">
                                    {title}
                                </p>
                                <p className="text-base text-gray-600">{subtitle}</p>
                            </div>
                        </motion.div>
                    ))}
                    <motion.p
                        className="text-center text-2xl text-gray-800"
                        variants={itemVariants}
                    >
                        Structure <em>and</em> flexibility.
                    </motion.p>
                </motion.div>
            ),
            next: {
                label: "Yes please",
            },
        }))
        .with("email", () => {
            const mode = values.mode ?? "track";
            const modeConfig = match(mode)
                .with("observe", () => ({
                    subtitle: "We use this to save your progress",
                    extraContent: (
                        <div className="mt-4 space-y-2 text-center text-base font-normal whitespace-nowrap text-gray-800">
                            <p>Your learning week is on us.</p>
                            <p>Upgrade later for insights and guidance.</p>
                        </div>
                    ),
                    register: handleLearningWeekRegistration,
                }))
                .with("track", () => ({
                    subtitle: "We use this to save your progress",
                    extraContent: (
                        <div className="mt-4 space-y-2 text-center text-lg font-normal whitespace-nowrap text-gray-800">
                            <p>Joy tracks nutrition, workouts and habits.</p>
                            <p>It's free for 7 days.</p>
                            <p>
                                Afterwards, it's just ${subscriptionDollarsPerMonth} a
                                month.
                            </p>
                        </div>
                    ),
                    register: handleLegacyRegistration,
                }))
                .with("plan", () => ({
                    subtitle: "We use this to save your progress",
                    extraContent: (
                        <div className="mt-4 space-y-2 text-center text-base font-normal whitespace-nowrap text-gray-800">
                            <p>Your plan is free.</p>
                            <p>Upgrade anytime to track it inside Joy.</p>
                        </div>
                    ),
                    register: handlePlanRegistration,
                }))
                .with("mfp", () => ({
                    subtitle: "We use this to save your progress",
                    extraContent: null,
                    register: handleLegacyRegistration,
                }))
                .exhaustive();

            return {
                isSubmitting,
                animation: null,
                content: (
                    <motion.div
                        className="flex flex-1 flex-col justify-center"
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                    >
                        <motion.h1
                            className="mb-2 text-center text-4xl font-light text-pretty"
                            variants={itemVariants}
                        >
                            Create your account
                        </motion.h1>
                        <motion.p
                            className="mb-8 text-center text-lg text-gray-600"
                            variants={itemVariants}
                        >
                            {modeConfig.subtitle}
                        </motion.p>

                        <motion.div variants={itemVariants}>
                            <form
                                noValidate
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    if (document.activeElement instanceof HTMLElement) {
                                        document.activeElement.blur();
                                    }
                                }}
                            >
                                <input type="submit" hidden />
                                <label className="relative block">
                                    <forms.Widget
                                        field={emailForm.fields.email}
                                        className={classNames(
                                            "px-4 py-4 rounded-xl border border-stone-300 w-full text-lg bg-white/80",
                                            emailForm.errors.email != null &&
                                                "border-red-300! bg-red-50",
                                        )}
                                    />
                                    <p className="absolute inset-x-0 -bottom-6 text-center text-sm font-normal text-red-600">
                                        {emailForm.errors.email}
                                    </p>
                                </label>
                            </form>
                        </motion.div>
                        {modeConfig.extraContent}
                    </motion.div>
                ),
                footer: (
                    <p className="-mt-1 mb-1 text-center text-xs text-gray-500">
                        Continuing means you agree to our{" "}
                        <a
                            href="https://www.joyapp.com/terms-of-service/"
                            className="underline"
                        >
                            terms and conditions
                        </a>
                    </p>
                ),
                next: {
                    // Custom onNext - async registration
                    onNext: async () => {
                        void hardware.haptic();
                        const validated = emailForm.validated;
                        if (!validated.success) return;
                        const revision = emailForm.revision;
                        const response = await server.core.rpc.check_email(
                            validated.data.email,
                        );

                        if (response.type === "invalid") {
                            emailForm.setServerErrors(
                                {
                                    email: ["Please enter a valid email address"],
                                },
                                {revision},
                            );
                            return;
                        }

                        if (response.type !== "success") {
                            alert("Error checking email");
                            return;
                        }

                        if (response.data !== false) {
                            emailForm.setServerErrors(
                                {
                                    email: [
                                        "This email is already registered. Login instead.",
                                    ],
                                },
                                {revision},
                            );
                            return;
                        }

                        await modeConfig.register(validated.data.email);
                    },
                    label: "Create account",
                    disabled: emailForm.values.email.trim().includes("@") === false,
                },
            };
        })
        .with("start", () => ({
            animation: null,
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.div variants={itemVariants}>
                        {values.mode === "mfp" ? (
                            <BreakUp className="mb-8 h-[60dvh] w-full" />
                        ) : (
                            <StopWastingTime className="mb-8 h-[60dvh] w-full" />
                        )}
                    </motion.div>
                </motion.div>
            ),
            footer: (
                <div className="flex justify-center">
                    <p className="text-base">
                        Returning user?{" "}
                        <router.Link
                            route="login.index"
                            params={{}}
                            className="font-normal"
                        >
                            Login
                        </router.Link>
                    </p>
                </div>
            ),
            next: {
                label: "Get Started",
                onNext:
                    values.mode === "mfp"
                        ? () => {
                              void hardware.haptic();
                              setRoute("mfp_promise", "next");
                          }
                        : undefined,
            },
        }))
        .with("goal", () => ({
            animation: (
                <Lottie
                    animationData={setGoalAnimation}
                    loop={true}
                    className="size-40 rounded-full bg-indigo-100 p-3"
                />
            ),
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        id={`${ids}-goal`}
                        className="mb-2 text-center text-4xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        What's your goal?
                    </motion.h1>
                    <motion.p
                        id={`${ids}-goal-help`}
                        className="mb-8 text-center text-lg text-gray-600"
                        variants={itemVariants}
                    >
                        We'll build your plan around this.
                    </motion.p>

                    <motion.div variants={itemVariants}>
                        <profile.BigRadioSelect
                            aria-labelledby={`${ids}-goal`}
                            aria-describedby={`${ids}-goal-help`}
                            field={onboardingForm.fields.goal}
                            descriptions={{
                                lose: "Lose around a pound a week.",
                                maintain: "Eat healthier and maintain.",
                                gain: "Put on muscle with minimal fat.",
                            }}
                        />
                    </motion.div>
                </motion.div>
            ),
            next: {
                label: "Continue",
                disabled: onboardingForm.values.goal == null,
            },
        }))
        .with("framing", () => ({
            animation: tasksAnimationElement,
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center gap-6"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        className="text-center text-4xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        Let's plan the next few weeks
                    </motion.h1>
                    {[
                        {
                            text: "Be realistic about what you'll do",
                            Icon: LightBulbIcon,
                            bg: "bg-amber-100",
                            color: "text-amber-600",
                        },
                        {
                            text: "We'll build a plan that fits real life",
                            Icon: WrenchScrewdriverIcon,
                            bg: "bg-indigo-100",
                            color: "text-indigo-600",
                        },
                        {
                            text: "Nothing here is permanent",
                            Icon: ArrowPathIcon,
                            bg: "bg-green-100",
                            color: "text-green-600",
                        },
                    ].map(({text, Icon, bg, color}) => (
                        <motion.div
                            key={text}
                            className="flex items-center gap-3 rounded-2xl bg-white p-4"
                            variants={itemVariants}
                        >
                            <div
                                className={classNames(
                                    "size-10 rounded-full flex items-center justify-center shrink-0",
                                    bg,
                                )}
                            >
                                <Icon className={classNames("size-5", color)} />
                            </div>
                            <p className="text-base text-gray-700">{text}</p>
                        </motion.div>
                    ))}
                </motion.div>
            ),
            next: {
                label: "Got it",
            },
        }))
        .with("cooking", () => ({
            animation: (
                <Lottie
                    animationData={cookingEggAnimation}
                    loop={true}
                    className="size-40 rotate-90 rounded-full bg-indigo-100 p-6"
                />
            ),
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        id={`${ids}-cooking`}
                        className="mb-2 text-center text-4xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        How often can you cook?
                    </motion.h1>
                    <motion.p
                        id={`${ids}-cooking-help`}
                        className="mb-8 text-center text-lg text-gray-600"
                        variants={itemVariants}
                    >
                        Cooking = stove, oven, air fryer, etc.
                    </motion.p>

                    <motion.div variants={itemVariants}>
                        <profile.BigRadioSelect
                            aria-labelledby={`${ids}-cooking`}
                            aria-describedby={`${ids}-cooking-help`}
                            field={onboardingForm.fields.cookingFrequency}
                            descriptions={{
                                never: undefined,
                                occasionally: undefined,
                                often: undefined,
                            }}
                        />
                    </motion.div>
                </motion.div>
            ),
            next: {
                label: "Continue",
                disabled: onboardingForm.values.cookingFrequency == null,
                // Custom onNext - sets assemblyWilling if they cook
                onNext: () => {
                    void hardware.haptic();
                    if (onboardingForm.values.cookingFrequency !== "never") {
                        onboardingForm.fields.assemblyWilling.onChange("yes");
                    }
                    if (flowNav.next != null) {
                        setRoute(flowNav.next, "next");
                    }
                },
            },
        }))
        .with("assembly", () => ({
            animation: (
                <Lottie
                    animationData={saladAnimation}
                    loop={true}
                    className="size-40 rounded-full bg-indigo-100 p-6"
                />
            ),
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        id={`${ids}-assembly`}
                        className="mb-2 text-center text-4xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        Are you okay assembling food?
                    </motion.h1>
                    <motion.p
                        id={`${ids}-assembly-help`}
                        className="mb-2 text-center text-lg text-gray-600"
                        variants={itemVariants}
                    >
                        No cooking. Just combining ingredients.
                    </motion.p>
                    <motion.p
                        id={`${ids}-assembly-examples`}
                        className="mb-8 text-center text-base text-gray-600"
                        variants={itemVariants}
                    >
                        Bowls, sandwiches, yogurt, rotisserie chicken
                    </motion.p>

                    <motion.div variants={itemVariants}>
                        <profile.BigRadioSelect
                            aria-labelledby={`${ids}-assembly`}
                            aria-describedby={`${ids}-assembly-help ${ids}-assembly-examples`}
                            field={onboardingForm.fields.assemblyWilling}
                            descriptions={{
                                yes: undefined,
                                no: undefined,
                            }}
                        />
                    </motion.div>
                </motion.div>
            ),
            next: {
                label: "Continue",
                disabled: onboardingForm.values.assemblyWilling == null,
            },
        }))
        .with("equipment", () => ({
            animation: (
                <Lottie
                    animationData={ovenAnimation}
                    loop={true}
                    className="size-40 rounded-full bg-indigo-100 p-6"
                />
            ),
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        className="mb-2 text-center text-4xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        What do you have access to?
                    </motion.h1>
                    <motion.p
                        className="mb-6 text-center text-lg text-pretty text-gray-600"
                        variants={itemVariants}
                    >
                        We'll only suggest meals that fit your setup.
                    </motion.p>

                    <div className="grid grid-cols-2 gap-2">
                        <motion.div variants={itemVariants}>
                            <CheckboxOption
                                label="Microwave"
                                checked={arrayValues.equipment.includes("microwave")}
                                onChange={() =>
                                    toggleArrayItem("equipment", "microwave")
                                }
                            />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <CheckboxOption
                                label="Stove"
                                checked={arrayValues.equipment.includes("stove")}
                                onChange={() => toggleArrayItem("equipment", "stove")}
                            />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <CheckboxOption
                                label="Oven"
                                checked={arrayValues.equipment.includes("oven")}
                                onChange={() => toggleArrayItem("equipment", "oven")}
                            />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <CheckboxOption
                                label="Air fryer"
                                checked={arrayValues.equipment.includes("airfryer")}
                                onChange={() =>
                                    toggleArrayItem("equipment", "airfryer")
                                }
                            />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <CheckboxOption
                                label="Rice cooker"
                                checked={arrayValues.equipment.includes("ricecooker")}
                                onChange={() =>
                                    toggleArrayItem("equipment", "ricecooker")
                                }
                            />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <CheckboxOption
                                label="Kitchen scale"
                                checked={arrayValues.equipment.includes("scale")}
                                onChange={() => toggleArrayItem("equipment", "scale")}
                            />
                        </motion.div>
                    </div>

                    <HelperText>No equipment is totally fine. We'll adjust.</HelperText>
                </motion.div>
            ),
            next: {
                label: "Continue",
            },
        }))
        .with("repetition", () => ({
            animation: (
                <Lottie
                    animationData={eatRepeatAnimation}
                    loop={true}
                    className="size-40 overflow-hidden rounded-full bg-indigo-100"
                />
            ),
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        id={`${ids}-repetition`}
                        className="mb-2 text-center text-4xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        Can you eat the same meals multiple days?
                    </motion.h1>
                    <motion.p
                        id={`${ids}-repetition-help`}
                        className="mb-8 text-center text-lg text-gray-600"
                        variants={itemVariants}
                    >
                        Repeating meals makes this easier.
                    </motion.p>

                    <motion.div variants={itemVariants}>
                        <profile.BigRadioSelect
                            aria-labelledby={`${ids}-repetition`}
                            aria-describedby={`${ids}-repetition-help`}
                            field={onboardingForm.fields.repetitionTolerance}
                            descriptions={{
                                none: undefined,
                                few: undefined,
                                week: undefined,
                            }}
                        />
                    </motion.div>

                    <HelperText>You can change this later.</HelperText>
                </motion.div>
            ),
            next: {
                label: "Continue",
                disabled: onboardingForm.values.repetitionTolerance == null,
            },
        }))
        .with("eating_modes", () => ({
            animation: (
                <Lottie
                    animationData={eatingAnimation}
                    loop={true}
                    className="size-40 rounded-full bg-indigo-100"
                />
            ),
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        className="mb-2 text-center text-4xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        How do you typically eat in a normal week?
                    </motion.h1>
                    <motion.p
                        className="mb-6 text-center text-lg text-gray-600"
                        variants={itemVariants}
                    >
                        Select all that apply.
                    </motion.p>

                    <div className="flex flex-col gap-2">
                        <motion.div variants={itemVariants}>
                            <CheckboxOption
                                label="Cook meals"
                                checked={arrayValues.eatingModes.includes("cook")}
                                onChange={() => toggleArrayItem("eatingModes", "cook")}
                            />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <CheckboxOption
                                label="Meal prep"
                                checked={arrayValues.eatingModes.includes("prep")}
                                onChange={() => toggleArrayItem("eatingModes", "prep")}
                            />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <CheckboxOption
                                label="Assemble simple food"
                                checked={arrayValues.eatingModes.includes("assemble")}
                                onChange={() =>
                                    toggleArrayItem("eatingModes", "assemble")
                                }
                            />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <CheckboxOption
                                label="Order food"
                                checked={arrayValues.eatingModes.includes("food")}
                                onChange={() => toggleArrayItem("eatingModes", "food")}
                            />
                        </motion.div>
                        <HelperText>Most people do a mix. That's normal.</HelperText>
                    </div>
                </motion.div>
            ),
            next: {
                label: "Continue",
                disabled: arrayValues.eatingModes.length === 0,
            },
        }))
        .with("nights_out", () => ({
            animation: (
                <Lottie
                    animationData={dayNightAnimation}
                    loop={true}
                    className="size-40 rounded-full bg-indigo-100 p-6"
                />
            ),
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        className="mb-2 text-center text-4xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        How many nights out in a normal week?
                    </motion.h1>
                    <motion.p
                        className="mb-8 text-center text-lg text-gray-600"
                        variants={itemVariants}
                    >
                        We plan for this. It's not cheating.
                    </motion.p>

                    <motion.div className="flex gap-3" variants={itemVariants}>
                        {onboardingForm.fields.nightsOut.options.map(
                            ([value, label]) => {
                                if (value == null) return null;
                                return (
                                    <button
                                        key={value}
                                        onClick={() => {
                                            void hardware.haptic();
                                            onboardingForm.fields.nightsOut.onChange(
                                                value,
                                            );
                                        }}
                                        style={
                                            onboardingForm.values.nightsOut === value
                                                ? {
                                                      borderColor: "#4F46E5",
                                                      backgroundColor:
                                                          "rgba(79, 70, 229, 0.05)",
                                                  }
                                                : {}
                                        }
                                        className={classNames(
                                            "flex-1 aspect-square rounded-2xl border-2 text-2xl font-bold transition-all flex items-center justify-center",
                                            onboardingForm.values.nightsOut === value
                                                ? "text-indigo-600"
                                                : "border-stone-200 bg-white text-gray-900",
                                        )}
                                    >
                                        {label}
                                    </button>
                                );
                            },
                        )}
                    </motion.div>
                </motion.div>
            ),
            next: {
                label: "Continue",
                disabled: onboardingForm.values.nightsOut == null,
            },
        }))
        .with("expectation", () => ({
            animation: (
                <Lottie
                    animationData={rubiksCubeAnimation}
                    loop={true}
                    className="size-40 rounded-full bg-indigo-100"
                />
            ),
            content: (
                <motion.div
                    className="flex flex-1 flex-col justify-center"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <div className="flex flex-1 flex-col justify-center">
                        <motion.h1
                            className="mb-6 text-center text-4xl font-light text-pretty"
                            variants={itemVariants}
                        >
                            One more thing.
                        </motion.h1>

                        <motion.div
                            className="mb-6 rounded-2xl bg-white p-6 text-center text-pretty"
                            variants={itemVariants}
                        >
                            <p className="mb-4 text-xl/snug font-semibold text-gray-900">
                                This plan is intentionally boring.
                            </p>
                            <p className="mb-6 text-xl font-semibold text-indigo-600">
                                That's why it works.
                            </p>

                            <div className="mx-auto inline-flex flex-col gap-3">
                                <ExpectationRow text="You'll eat similar meals" />
                                <ExpectationRow text="You'll stop thinking about food" />
                                <ExpectationRow text="You'll get results" />
                            </div>
                        </motion.div>

                        <motion.p
                            className="text-center text-base text-gray-600"
                            variants={itemVariants}
                        >
                            This is a short, focused plan.
                            <br />
                            We'll adapt it as you go.
                        </motion.p>
                    </div>
                </motion.div>
            ),
            next: {
                label: "Sounds good",
            },
        }))
        .with("confirmation", () => {
            const goalText = {
                lose: "Fat loss",
                maintain: "Maintenance",
                gain: "Muscle gain",
            };
            const repetitionText = {
                week: "All week OK",
                few: "Few days OK",
                none: "Need variety",
            };

            return {
                animation: (
                    <Lottie
                        animationData={rocketAnimation}
                        loop={true}
                        className="current-color-lottie size-40 rounded-full bg-indigo-100 fill-indigo-100 stroke-indigo-100 p-6 text-indigo-600"
                    />
                ),
                content: (
                    <motion.div
                        className="flex flex-1 flex-col justify-center"
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                    >
                        <motion.h1
                            className="mb-2 text-center text-4xl font-light text-pretty"
                            variants={itemVariants}
                        >
                            Your plan is ready.
                        </motion.h1>
                        <motion.p
                            className="mb-6 text-center text-lg text-gray-600"
                            variants={itemVariants}
                        >
                            You can change anything later.
                        </motion.p>

                        <motion.div
                            className="rounded-2xl bg-white p-5 shadow-sm"
                            variants={itemVariants}
                        >
                            <SummaryRow
                                icon="🎯"
                                label="Goal"
                                value={
                                    onboardingForm.values.goal != null
                                        ? goalText[onboardingForm.values.goal]
                                        : null
                                }
                            />
                            <SummaryRow
                                icon="🍳"
                                label="Cooking"
                                value={onboardingForm.values.cookingFrequency}
                            />
                            <SummaryRow
                                icon="🔄"
                                label="Repetition"
                                value={
                                    onboardingForm.values.repetitionTolerance != null
                                        ? repetitionText[
                                              onboardingForm.values.repetitionTolerance
                                          ]
                                        : null
                                }
                            />
                            <SummaryRow
                                icon="🌙"
                                label="Nights out"
                                value={
                                    onboardingForm.values.nightsOut != null
                                        ? `${onboardingForm.values.nightsOut} per week`
                                        : null
                                }
                            />

                            <div className="mt-4 border-t border-gray-200 pt-4">
                                <div className="mb-2 flex items-center gap-2.5">
                                    <span className="text-base">📋</span>
                                    <span className="text-sm font-semibold text-gray-900">
                                        Included
                                    </span>
                                </div>
                                <div className="ml-6 flex flex-wrap gap-1.5">
                                    <MiniTag>Macros calculated</MiniTag>
                                    <MiniTag>Meals selected</MiniTag>
                                    <MiniTag>Flex budget added</MiniTag>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                ),
                next: {
                    label: "Show me the plan",
                    // Custom onNext - final validated
                    onNext: () => {
                        void hardware.haptic();
                        // For now, just log the data - no backend integration yet
                        console.log("Onboarding complete:", {
                            ...onboardingForm.values,
                            ...arrayValues,
                        });
                        alert("Onboarding complete! (Data stored in React state only)");
                    },
                },
            };
        })
        .with(
            "mfp_promise",
            "mfp_credentials",
            "mfp_why",
            "mfp_importing",
            "mfp_pitch",
            "mfp_confirm",
            () => mfpFlow,
        )
        .exhaustive();

    return {
        route,
        animation: config.animation,
        content: config.content,
        footer: config.footer,
        isSubmitting: config.isSubmitting,
        back: goBack,
        next: {
            onNext: config.next.onNext ?? defaultOnNext,
            label: config.next.label,
            icon: config.next.icon ?? ArrowRightIcon,
            iconPosition: config.next.iconPosition ?? "right",
            animateIcon: config.next.animateIcon ?? true,
            disabled: config.next.disabled ?? false,
        },
        progress: {
            current: flowNav.progressIndex,
            total: flowNav.totalProgress,
        },
    };
}

function OnboardingScreenContent({
    scrollY,
    hasOverflow,
    animation,
    back,
    content,
    isSubmitting,
    next,
    footer,
    route,
    progress,
}: OnboardingScreen & {scrollY: MotionValue<number>; hasOverflow: boolean}) {
    const backgroundColor = useTransform(
        scrollY,
        [0, 50],
        ["rgba(252, 249, 244, 0)", "rgba(252, 249, 244, 0.95)"],
        {clamp: true},
    );
    const backdropFilter = useTransform(scrollY, [0, 50], ["blur(0px)", "blur(8px)"], {
        clamp: true,
    });

    return (
        <>
            <motion.div
                className="pt-safe-or-padding sticky top-0 z-10"
                style={{backgroundColor, backdropFilter}}
            >
                <div className="flex items-center justify-center py-2">
                    {back != null && (
                        <button
                            className="absolute left-4"
                            onClick={() => {
                                void hardware.haptic();
                                back();
                            }}
                        >
                            <SimpleIcon
                                icon="custom-chevron-left"
                                className="size-5 text-black"
                            />
                        </button>
                    )}
                    <graphics.Logo className="h-6" />
                </div>
            </motion.div>
            {animation != null && (
                <div className="mt-5 -mb-5 flex items-center justify-center">
                    {animation}
                </div>
            )}
            <div className="flex flex-1 flex-col justify-center px-6">
                <AnimatePresence mode="popLayout">
                    <motion.div
                        key={route}
                        variants={contentTransitionVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{duration: 0.2, ease: "easeOut"}}
                        className="flex flex-1 flex-col justify-center"
                    >
                        {content}
                    </motion.div>
                </AnimatePresence>
            </div>
            <div className="pb-safe-or-padding sticky bottom-0 z-10 flex flex-col items-center justify-center gap-1 px-6">
                <div className={classNames("py-1", hasOverflow && "invisible")}>
                    {footer}
                </div>
                <AnimatedButton
                    label={next.label}
                    icon={next.icon}
                    iconPosition={next.iconPosition}
                    animateIcon={next.animateIcon}
                    onClick={next.onNext}
                    isSubmitting={isSubmitting}
                    disabled={next.disabled}
                />
                <div
                    className={classNames(
                        "pt-3",
                        (progress.current == null || hasOverflow) && "invisible",
                    )}
                >
                    <ProgressDots
                        current={progress.current ?? 0}
                        total={progress.total}
                    />
                </div>
            </div>
        </>
    );
}

export function OnboardingScreenComponent(screen: OnboardingScreen) {
    const {animation, back, content, isSubmitting, next, footer, route, progress} =
        screen;
    return (
        <Shell>
            {({scrollY, hasOverflow}) => (
                <OnboardingScreenContent
                    scrollY={scrollY}
                    hasOverflow={hasOverflow}
                    animation={animation}
                    back={back}
                    content={content}
                    isSubmitting={isSubmitting}
                    next={next}
                    footer={footer}
                    route={route}
                    progress={progress}
                />
            )}
        </Shell>
    );
}

function PlanOnboardingContent(props: {joyTag: string | null}) {
    const screen = useOnboarding({joyTag: props.joyTag});

    React.useEffect(() => {
        /*
        void hardware.nativeOnly(() => {
            void hardware.Keyboard.setResizeMode({mode: hardware.KeyboardResize.Body});
        });
        return () => {
            void hardware.nativeOnly(() => {
                void hardware.Keyboard.setResizeMode({
                    mode: hardware.KeyboardResize.Native,
                });
            });
        };
        */
    }, []);

    return <OnboardingScreenComponent {...screen} />;
}

export const PlanOnboarding = router.PlanOnboarding.withHooks((props) => ({
    joyTag: props.resolved.plan_onboarding.joyTag,
}))
    .withIndex(PlanOnboardingContent)
    .withActions(() => [])
    .withOptions(() => ({
        title: "Joy Health Tracker",
        scene: {kind: "EMPTY"},
    }));

function PlanWaitlistContent(props: {profile: server.core.rpc.Profile.output}) {
    const {status: pushStatus, register: registerPushNotifications} =
        push.usePushNotifications({profile: props.profile});

    const handleEnablePush = async () => {
        void hardware.haptic();
        await registerPushNotifications();
    };

    return (
        <OnboardingScreenComponent
            animation={
                <Lottie
                    animationData={loadingAnimation}
                    loop={true}
                    className="size-40 rounded-full bg-indigo-100 p-2 *:opacity-70"
                />
            }
            back={null}
            content={
                <motion.div
                    className="flex flex-1 flex-col justify-center gap-6"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        className="text-center text-3xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        We're crafting your plan
                    </motion.h1>

                    <motion.p
                        className="text-center text-lg text-gray-800"
                        variants={itemVariants}
                    >
                        Built around your habits.
                        <br />
                        Reviewed by hand.
                    </motion.p>

                    {[
                        {
                            text: "We have your info",
                            Icon: DocumentArrowUpIcon,
                            bg: "bg-green-100",
                            color: "text-green-600",
                        },
                        {
                            text: "We'll create a customized plan",
                            Icon: ClockIcon,
                            bg: "bg-indigo-100",
                            color: "text-indigo-600",
                        },
                        {
                            text: "Then we'll email you",
                            Icon: EnvelopeIcon,
                            bg: "bg-amber-100",
                            color: "text-amber-600",
                        },
                    ].map(({text, Icon, bg, color}, index) => (
                        <motion.div
                            key={index}
                            className="flex items-center gap-3 rounded-2xl border border-stone-200/50 bg-stone-100/70 p-4 backdrop-blur-sm"
                            variants={itemVariants}
                        >
                            <div
                                className={classNames(
                                    "size-10 rounded-full flex items-center justify-center shrink-0",
                                    bg,
                                )}
                            >
                                <Icon className={classNames("size-5", color)} />
                            </div>
                            <p className="text-lg font-normal text-gray-800">{text}</p>
                        </motion.div>
                    ))}
                </motion.div>
            }
            next={{
                onNext: handleEnablePush,
                label: pushStatus === "granted" ? "Notifications on" : "Notify me",
                icon: pushStatus === "granted" ? CheckIcon : BellIcon,
                iconPosition: "left",
                animateIcon: true,
                disabled: pushStatus !== "prompt",
            }}
            route="start"
            progress={{current: null, total: 0}}
        />
    );
}

export function usePlanWaitlist(props: {profile: server.core.rpc.Profile.output}): {
    view: React.ReactNode;
    actions: [];
    options: {
        titleSection: React.ReactNode;
        title: string;
        scene: {kind: "EMPTY"};
    };
} {
    return {
        view: <PlanWaitlistContent profile={props.profile} />,
        actions: [],
        options: {
            titleSection: <graphics.Logo className="-mb-2 h-9 w-auto py-1" />,
            title: "You're on the list",
            scene: {kind: "EMPTY"},
        },
    };
}
